import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_ADD_MODES,
  CAMPAIGN_DESTINATIONS,
  CAMPAIGN_JOIN_MODES,
  CampaignModalSection,
  CampaignModalShell,
  CampaignModalSummary,
  CampaignOptionCard,
} from '#/entities/campaign'
import type { CampaignAddMode } from '#/entities/campaign'
import type {
  CampaignAudienceQuery,
  CampaignPeopleAdd,
} from '#/shared/api/generated/communication/models'
import { Input } from '#/shared/ui/input'
import { Select } from '#/shared/ui/select'

import { toCampaignCreate } from '../lib/to-campaign-create'
import { hasPeople, useAddToCampaign } from '../model/use-add-to-campaign'
import type { UseAddToCampaignResult } from '../model/use-add-to-campaign'
import { useCampaignAudienceCount } from '../model/use-campaign-audience-count'
import { useCampaignEnrolment } from '../model/use-campaign-enrolment'
import {
  CampaignScopeSection,
  CampaignTriggerFields,
} from './campaign-enrolment-fields'

interface AddToCampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Who joins — ticked contacts, or a quote-list selection. */
  people: CampaignPeopleAdd
  /** How many the user picked, for the copy. A filter selection's count is
   *  the number the list showed; the service reconciles the rest. */
  count: number
  /** The campaign they are already in, which is not offered as a destination. */
  excludeCampaignId?: string
  /** After a successful add — clear the selection behind the dialog. */
  onAdded?: () => void
}

/**
 * The people are picked, not projected, so the count runs over them rather
 * than a rule — by customer id from the contact list, or as the same
 * selection object the quote list sends to every other mass action. Naming
 * the campaign they are headed for is what turns `already_enrolled` from a
 * guess into a fact.
 */
function toSelectionAudience(
  people: CampaignPeopleAdd,
  destinationId: string,
): CampaignAudienceQuery {
  return {
    ...(people.shipment_selection != null
      ? { shipment_selection: people.shipment_selection }
      : {
          customer_ids: (people.contacts ?? []).flatMap((contact) =>
            contact.customer_id == null ? [] : [contact.customer_id],
          ),
        }),
    ...(destinationId === '' ? {} : { campaign_id: destinationId }),
  }
}

/**
 * Where the people land. The picker and the name field sit inside their own
 * cards, so only the chosen one is asked for — the select is portaled because
 * the card it sits in scrolls inside the dialog.
 */
function DestinationSection({ add }: { add: UseAddToCampaignResult }) {
  const { t } = useTranslation()
  return (
    <CampaignModalSection label={t('campaigns.modal.add.destinationLabel')}>
      <CampaignOptionCard
        title={t('campaigns.modal.add.existing.title')}
        description={t('campaigns.modal.add.existing.description')}
        selected={add.destination === CAMPAIGN_DESTINATIONS.existing}
        onSelect={() => add.setDestination(CAMPAIGN_DESTINATIONS.existing)}
      >
        <Select
          portal
          selectSize="sm"
          options={add.options}
          loading={add.isLoadingOptions}
          value={add.campaignId}
          placeholder={t('campaigns.modal.add.existing.placeholder')}
          onValueChange={add.setCampaignId}
        />
      </CampaignOptionCard>
      <CampaignOptionCard
        title={t('campaigns.modal.add.new.title')}
        description={t('campaigns.modal.add.new.description')}
        selected={add.destination === CAMPAIGN_DESTINATIONS.new}
        onSelect={() => add.setDestination(CAMPAIGN_DESTINATIONS.new)}
      >
        <Input
          value={add.name}
          placeholder={t('campaigns.modal.new.namePlaceholder')}
          onChange={(event) => add.setName(event.target.value)}
        />
      </CampaignOptionCard>
    </CampaignModalSection>
  )
}

/**
 * Dropping a selection into a campaign — from the contact list's bulk bar, or
 * from the Quote list's.
 *
 * It asks the same three questions as the new-campaign dialog and shares its
 * parts; what differs is that the people are already known, so the box at the
 * bottom reports on them rather than replaying a rule over a past window.
 */
export function AddToCampaignDialog({
  open,
  onOpenChange,
  people,
  count,
  excludeCampaignId,
  onAdded,
}: AddToCampaignDialogProps) {
  const { t } = useTranslation()
  const enrolment = useCampaignEnrolment()
  const [mode, setMode] = useState<CampaignAddMode>(
    CAMPAIGN_ADD_MODES.keepAdding,
  )
  const add = useAddToCampaign({
    people,
    excludeCampaignId,
    enabled: open,
    onAdded: () => {
      onOpenChange(false)
      onAdded?.()
    },
  })

  const audience = useCampaignAudienceCount({
    enabled: open && hasPeople(people),
    query: toSelectionAudience(people, add.campaignId),
  })

  return (
    <CampaignModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('campaigns.modal.add.title', { count })}
      description={t('campaigns.modal.add.description', { count })}
      submitLabel={t('campaigns.modal.add.submit', { count })}
      submitDisabled={!add.canSubmit || add.isAdding}
      footerNote={t('campaigns.modal.channelNote')}
      onSubmit={() =>
        add.onSubmit(
          toCampaignCreate({
            name: add.name,
            scope: enrolment.scope,
            // "Only these people" is a one-shot list; "keep adding" is the
            // same trigger the new-campaign dialog offers.
            join:
              mode === CAMPAIGN_ADD_MODES.onlyThese
                ? CAMPAIGN_JOIN_MODES.manual
                : CAMPAIGN_JOIN_MODES.trigger,
            trigger: enrolment.trigger,
            filterDefinition: {},
            userIds: enrolment.appliesToUserIds,
            providerIds: enrolment.selectedProviderIds,
          }),
        )
      }
    >
      <DestinationSection add={add} />

      <CampaignScopeSection state={enrolment} />

      <CampaignModalSection label={t('campaigns.modal.join.label')}>
        <CampaignOptionCard
          title={t('campaigns.modal.add.onlyThese.title', { count })}
          description={t('campaigns.modal.add.onlyThese.description')}
          selected={mode === CAMPAIGN_ADD_MODES.onlyThese}
          onSelect={() => setMode(CAMPAIGN_ADD_MODES.onlyThese)}
        />
        <CampaignOptionCard
          title={t('campaigns.modal.add.keepAdding.title')}
          description={t('campaigns.modal.add.keepAdding.description')}
          selected={mode === CAMPAIGN_ADD_MODES.keepAdding}
          onSelect={() => setMode(CAMPAIGN_ADD_MODES.keepAdding)}
        >
          <CampaignTriggerFields state={enrolment} />
        </CampaignOptionCard>
      </CampaignModalSection>

      <CampaignModalSummary
        label={
          audience.basis ?? t('campaigns.modal.add.summaryLabel', { count })
        }
        headline={audience.headline}
        notes={[...audience.notes, t('campaigns.modal.add.notes.skipOnly')]}
      />
    </CampaignModalShell>
  )
}
